/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as Camera from './module';
import { css, SerializedStyles } from '@emotion/react';
import { getResource } from '../language/resources/getResource';


/**
 * Shamelessly pilfered from https://github.com/mozmorris/react-webcam
 * A component for interfacing with WebCams through the browser. Minimal frills; some abstraction.
 */
export default class WebCam extends React.Component<{
  onHasImage: (capture: string | undefined) => void,
  rootCss?: SerializedStyles,
  height?: number,
  width?: number,
  screenshotWidth?: number,
  screenshotHeight?: number,
  style?: object,
  screenshotFormat?: 'image/webp' | 'image/png' | 'image/jpeg',
  onConnectCam?: () => void,
  onUserMediaError: (MediaStreamError) => void,
  screenshotQuality?: number,
  videoConstraints?: {
    deviceId?: string,
    groupId?: string,
    aspectRatio?: number,
    facingMode?: 'user' | 'environment' | 'left' | 'right',
    frameRate?: number,
    height?: number,
    width?: number,
  },
  /**
   * Flip the camera stream along the X? Depending on the facing of the camera, this will fix left/right mirroring.
   */
  mirror?: boolean,
  resources: Record<string, any>,
}, {
  hasUserMedia: boolean,
  currentDevice?: string,
  src: string,
}> {
  public static defaultProps = {
    css:        css``,
    screenshotFormat: 'image/webp',
    onConnectCam:     () => {
    },
  };

  public state = {
    hasUserMedia: false,
    src:          '',
  };

  private userMediaRequested = false;
  private videoRef           = ref => this.video = ref;
  private ctx: CanvasRenderingContext2D | null;
  private video: HTMLVideoElement | null;
  private canvas: HTMLCanvasElement;
  private stream: MediaStream;


  public componentDidMount() {
    if (!Camera.canGetStream(window)) {
      this.props.onUserMediaError({ message: `${getResource(this.props.resources, 'WEBCAM_BROWSER_ERROR')}`});
      return;
    }

    if (!this.state.hasUserMedia && !this.userMediaRequested) {
      this.requestUserMedia();
    }
  }


  public componentDidUpdate(nextProps) {
    if (JSON.stringify(nextProps.videoConstraints) !== JSON.stringify(this.props.videoConstraints)) {
      this.tearDownStream();
      this.requestUserMedia();
    }
  }


  public componentWillUnmount() {
    this.tearDownStream();
  }


  private tearDownStream = () => {
    if (!this.stream) {
      return;
    }

    // stop is deprecated, so it's lost on the type def
    // @ts-ignore
    if (this.stream.stop) {
      // @ts-ignore
      this.stream.stop();

    } else {
      if (this.stream.getVideoTracks) {
        this.stream.getVideoTracks().map(track => track.stop());
      }

      if (this.stream.getAudioTracks) {
        this.stream.getAudioTracks().map(track => track.stop());
      }
    }

    this.userMediaRequested = false;
    window.URL.revokeObjectURL(this.state.src);
  };


  public getScreenshot = (): void => {
    if (!this.state.hasUserMedia) {
      return undefined;
    }

    const canvas = this.getCanvas();

    if (!canvas) {
      return undefined;
    }

    this.props.onHasImage(canvas.toDataURL(this.props.screenshotFormat, this.props.screenshotQuality));
  };


  private getCanvas = () => {
    if (!this.state.hasUserMedia || !this.video || !this.video.videoHeight) {
      return null;
    }

    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
    }

    if (!this.ctx) {
      const aspectRatio = this.video.videoWidth / this.video.videoHeight;

      this.canvas.width  = this.props.screenshotWidth || this.video.clientWidth;
      this.canvas.height = this.props.screenshotHeight || this.canvas.width / aspectRatio;

      this.ctx = this.canvas.getContext('2d');
    }

    // Draw WebCam image (or a subsection) onto the canvas for export to an image
    if (this.ctx) {
      // If mirror/reversed, need to flip the horizontal scale and draw to
      // a negative destination width
      if (this.props.mirror) {
        this.ctx.scale(-1, 1);
      }

      // Transfer frame from camera to a canvas for export to an image
      this.ctx.drawImage(
        this.video,
        (this.video.width - this.canvas.width) / 2, // Source X
        (this.video.height - this.canvas.height) / 2, // Source Y
        this.canvas.width, // Source Width
        this.canvas.height, // Source Height
        0, // Target X
        0, // Target Y
        this.props.mirror ? -this.canvas.width : this.canvas.width, // Target Width
        this.canvas.height, // Target Height
      );
    }

    return this.canvas;
  };


  private onReceiveCamResponse = (err, stream) => {
    if (err) {
      this.setState({ hasUserMedia: false });
      this.props.onUserMediaError(err);
      return;
    }

    if (!this.video || !stream) {
      this.setState({ hasUserMedia: false });
      this.props.onUserMediaError(getResource(this.props.resources, 'WEBCAM_STREAMING_ERROR'));
      return;
    }

    this.stream = stream;

    try {
      this.video.srcObject = stream;
      this.setState({ hasUserMedia: true });

    } catch (error) {
      this.setState(
        {
          hasUserMedia: true,
          src:          window.URL.createObjectURL(stream),
        },
      );
    }

    if (this.props.onConnectCam) {
      this.props.onConnectCam();
    }
  };


  private requestUserMedia = () => {
    const constraints = { video: this.props.videoConstraints || true };

    Camera.getStream(window.navigator, constraints)
      .then(stream => this.onReceiveCamResponse(null, stream))
      .catch(e => this.onReceiveCamResponse(e, null));

    this.userMediaRequested = true;
  };


  public render() {
    return <video ref={this.videoRef}
                  autoPlay={true}
                  width={this.props.width}
                  height={this.props.height}
                  src={this.state.src}
                  muted={false}
                  css={css`
                    ${this.props.mirror ? { transform: 'rotateY(180deg)' } : {}}
                    ${this.props.rootCss}
                  `}
                  playsInline={true}
                  style={this.props.style}/>;
  }
}
